import React, { useEffect, useRef } from 'react';
import { WALL_PANES } from './wallContent';
import './AIEFeatureBlocks.css';
import './AIEWall.css';

/**
 * WallFrame — ONE pane of the /ai-employees wall, rendered standalone
 * (Nitish, Sep 1 2026: /super-agents must use the SAME drive + avatar
 * components as the AIE wall — one source, every fix lands on both pages).
 *
 * Renders the pane's generated frameHtml inside a `.w-inline.w-standalone`
 * wrapper, so every wall rule scoped `:is(.w-slide,.w-inline)` (timings,
 * mobile recompositions, the drive icon-rail fix, the avatar tile flow)
 * applies verbatim. `.w-standalone` flips the base `.w-inline{display:none}`
 * to block at all widths (rule in AIEWall.css).
 *
 * Same restart principle as the wall's inline frames (s238 ruling): the
 * story restarts from t=0 whenever the frame scrolls into view, so nobody
 * catches an animation mid-cycle. Frame markup comes from wallContent.js —
 * generated by port_wall.py, never hand-edited.
 *
 * Pick the pane by its frame class (`cls`, e.g. "wm-drv", "wm-av", "wm-meet"),
 * never by number: numbers move whenever a pane is added or reordered. Sep 15
 * 2026 Meetings took "09" and /super-agents' Avatars section silently showed
 * the Meet frame until Oct 12 (Maeve). `num` stays as a fallback only.
 *
 * @param {string} cls  the pane's slideCls, e.g. "wm-drv" (Drive) or "wm-av" (Avatars)
 * @param {string} num  legacy: two-digit pane prefix; avoid
 * @param {string} html optional frame markup that is not a wall pane (Oct 12 2026: the Super Agents subagents
 *                      section renders a chat variant of the wall's Subagents frame with the same restart and classes)
 */
const WallFrame = ({ cls, num, html }) => {
  const ref = useRef(null);
  const pane = html
    ? { slideCls: cls || '', frameHtml: html }
    : WALL_PANES.find((p) => (cls ? p.slideCls === cls : p.num.startsWith(num)));

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting || !en.target.getAnimations) return;
        en.target.getAnimations({ subtree: true }).forEach((a) => {
          try { a.currentTime = 0; a.play(); } catch (e) { /* noop */ }
        });
      });
    }, { threshold: 0.25 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  if (!pane) return null;
  return (
    <div
      ref={ref}
      className={`w-inline w-standalone${pane.slideCls ? ` ${pane.slideCls}` : ''}`}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: pane.frameHtml }}
    />
  );
};

export default WallFrame;
